import { useNavigate } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  Zap, ArrowRight, Check, Rocket, ChevronRight, Radio, Lock,
} from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import api from "@/lib/api";
import { Products } from "@/components/landing/Products";
import { FeatureGrid } from "@/components/landing/FeatureGrid";
import { ForPlayers } from "@/components/landing/ForPlayers";
import { UpcomingTournaments } from "@/components/landing/UpcomingTournaments";

const LOGO = <BrandLogo height={32} compact />;

function Nav({ nav }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <div className="glass border-b" style={{ borderColor: "var(--border)" }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-8 h-16 flex items-center justify-between gap-2">
          <button data-testid="nav-logo" onClick={() => nav("/")} className="shrink-0">{LOGO}</button>
          <nav className="hidden md:flex items-center gap-8 text-sm" style={{ color: "var(--text-2)" }}>
            <a href="#upcoming" data-testid="nav-upcoming" className="hover:text-primary-c transition-colors">Tournaments</a>
            <a href="#services" data-testid="nav-services" className="hover:text-primary-c transition-colors">Services</a>
            <a href="#players" data-testid="nav-players" className="hover:text-primary-c transition-colors">For Players</a>
            <a href="#pricing" data-testid="nav-pricing" className="hover:text-primary-c transition-colors">Pricing</a>
            <button data-testid="nav-login" onClick={() => nav("/login")} className="hover:text-primary-c transition-colors">Login</button>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <ThemeToggle />
            <button data-testid="nav-signup" onClick={() => nav("/signup")} className="rounded-full px-3 sm:px-5 py-2 text-xs sm:text-sm font-bold glow-border hover:scale-[1.03] transition-transform" style={{ color: "var(--text)" }}>Sign up</button>
            <button
              data-testid="nav-launch-cta"
              onClick={() => nav("/login")}
              className="btn-glow rounded-full px-3 sm:px-5 py-2 text-xs sm:text-sm font-bold flex items-center gap-1 sm:gap-2"
            >
              Login <ArrowRight className="w-4 h-4 hidden sm:inline" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function Hero({ nav }) {
  return (
    <section className="relative pt-40 pb-24 overflow-hidden grain">
      <img
        src="https://images.unsplash.com/photo-1768330187404-59e46cf222c9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
        alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.22 }}
      />
      <div className="absolute inset-0 cyber-grid" style={{ opacity: 0.7 }} />
      <div className="absolute inset-0" style={{ background: "radial-gradient(1200px 500px at 20% -10%, var(--glow), transparent 70%)" }} />
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}
          className="max-w-4xl"
        >
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase font-bold text-primary-c glass rounded-full px-4 py-1.5">
            <Radio className="w-3.5 h-3.5" /> Racket Sports Command Center
          </span>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02]">
            Absolute Precision in <br className="hidden sm:block" />
            <span className="text-primary-c">Racket Sports</span> Management
          </h1>
          <p className="mt-6 text-base sm:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--text-2)" }}>
            Open-play rotations, pro tournaments, Club Wars team events and verified player ratings —
            all live, all in one platform. Fast at the courtside, cloud-synced for the record books.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <button data-testid="hero-launch-cta" onClick={() => nav("/login")}
              className="btn-glow rounded-full px-7 py-3.5 font-bold flex items-center gap-2">
              <Rocket className="w-5 h-5" /> Launch App
            </button>
            <a href="#services" data-testid="hero-explore"
              className="rounded-full px-7 py-3.5 font-bold flex items-center gap-2 glass hover:scale-[1.03] transition-transform"
              style={{ color: "var(--text)" }}>
              Explore Products <ChevronRight className="w-4 h-4" />
            </a>
          </div>
          <div className="mt-12 flex flex-wrap gap-8 text-sm" style={{ color: "var(--text-2)" }}>
            {[["FIFO", "Fair rotations"], ["0.0–6.0+", "Verified ratings"], ["Live", "TV scoreboards"], ["Offline", "Works anywhere"]].map(([a, b]) => (
              <div key={a}>
                <div className="font-display font-bold text-2xl text-primary-c">{a}</div>
                <div className="mono text-xs">{b}</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function AdMarquee() {
  const fallback = ["Summer Smash Open — Register Now", "Apex Racket Co. — 20% Off Members", "Night League Series — Fridays", "City Doubles Cup — 64 Draw", "Junior Development Camp"];
  const [items, setItems] = useState(null);

  useEffect(() => {
    api.get("/config/ads", { params: { zone: "landing" } })
      .then(({ data }) => setItems((data || []).map((a) => ({ title: a.title, link: a.link }))))
      .catch(() => setItems([]));
  }, []);

  const list = items && items.length > 0 ? items : fallback.map((t) => ({ title: t, link: null }));

  return (
    <section className="py-6 border-y grain" style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid="ad-marquee">
      <Marquee gradient={false} speed={40} pauseOnHover>
        {list.map((it, i) => {
          const inner = (
            <div className="flex items-center gap-3 mx-8">
              <Zap className="w-4 h-4 text-primary-c" />
              <span className="font-display font-bold text-sm tracking-tight">{it.title}</span>
              <span className="text-[10px] tracking-[0.3em] text-primary-c">· AD</span>
            </div>
          );
          return it.link ? <a key={i} href={it.link} target="_blank" rel="noreferrer">{inner}</a> : <div key={i}>{inner}</div>;
        })}
      </Marquee>
    </section>
  );
}

function Pricing({ nav }) {
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/config/pricing").then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  const sym = cfg?.currencies?.[cfg?.default_currency] || "$";
  const tiers = (cfg?.tiers || [
    { id: "event", name: "Pay-Per-Event", price: 35, unit: "/ event", desc: "Perfect for one-off open-play days and pop-up tournaments.", features: ["Open-Play Queue OS with Reclub import", "Unlimited courts & players", "Offline-first sessions", "Casual Play ratings for published sessions", "Sponsor ad slots"] },
    { id: "annual", name: "Club Annual License", price: 250, unit: "/ year", desc: "For clubs running weekly leagues and sanctioned events.", features: ["Everything in Pay-Per-Event", "Pro Tournament Suite — brackets, waitlists & branded emails", "Club Wars team events with captain portal", "Umpire scoring & live TV boards", "Player Database & community ratings", "Message players by email & in-app inbox", "Priority support"] },
  ]).map((t) => ({ ...t, highlight: t.id === "annual" }));

  const fmtPrice = (p) => `${sym}${Number(p || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <section id="pricing" className="max-w-6xl mx-auto px-5 sm:px-8 py-24">
      <div className="mb-14">
        <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c">Pricing</span>
        <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-3">Simple, transparent tiers.</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {tiers.map((t) => (
          <div key={t.id} data-testid={`pricing-${t.id}`}
            className={`relative rounded-3xl p-8 sm:p-10 bg-surface ${t.highlight ? "tracing-beam glow-border" : "glow-border"}`}>
            {t.highlight && (
              <span className="absolute top-6 right-6 text-[10px] tracking-[0.25em] font-bold text-primary-c glass rounded-full px-3 py-1">
                BEST VALUE
              </span>
            )}
            <h3 className="font-display font-bold text-xl">{t.name}</h3>
            <div className="mt-4 flex items-end gap-2">
              <span data-testid={`pricing-${t.id}-price`} className="font-display font-black text-5xl tracking-tighter">{fmtPrice(t.price)}</span>
              <span className="mono text-sm mb-1" style={{ color: "var(--text-2)" }}>{t.unit}</span>
            </div>
            <p className="mt-3 text-sm" style={{ color: "var(--text-2)" }}>{t.desc}</p>
            <ul className="mt-7 space-y-3">
              {(t.features || []).map((f) => (
                <li key={f} className="flex items-center gap-3 text-sm">
                  <span className="w-5 h-5 rounded-full glass flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-primary-c" />
                  </span>{f}
                </li>
              ))}
            </ul>
            <button data-testid={`pricing-${t.id}-cta`} onClick={() => nav("/login")}
              className={`mt-8 w-full rounded-full px-6 py-3 font-bold transition-transform hover:scale-[1.02] ${t.highlight ? "btn-glow" : "glass"}`}
              style={t.highlight ? {} : { color: "var(--text)" }}>
              {t.highlight ? "Get Club License" : "Start an Event"}
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}

function Footer({ nav }) {
  return (
    <footer className="border-t grain" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
        <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl">
          Let's <span className="text-primary-c">play.</span>
        </h2>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-8 text-sm" style={{ color: "var(--text-2)" }}>
          <div>
            {LOGO}
            <p className="mt-3">Absolute precision in racket sports management.</p>
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-bold" style={{ color: "var(--text)" }}>Product</span>
            <a href="#services" className="hover:text-primary-c transition-colors">Services</a>
            <a href="#players" className="hover:text-primary-c transition-colors">For Players</a>
            <a href="#pricing" className="hover:text-primary-c transition-colors">Pricing</a>
            <button onClick={() => nav("/login")} className="text-left hover:text-primary-c transition-colors">Login</button>
          </div>
          <div className="flex flex-col gap-2">
            <span className="font-bold" style={{ color: "var(--text)" }}>Legal & Support</span>
            <a href="#" className="hover:text-primary-c transition-colors">Terms of Service</a>
            <a href="#" className="hover:text-primary-c transition-colors">Privacy Policy</a>
            <a href="mailto:support@pointcontrol.app" className="hover:text-primary-c transition-colors">admin@pointcontrol.pro</a>
          </div>
        </div>
        <div className="mt-12 pt-6 border-t flex flex-wrap gap-2 justify-between items-center text-xs mono" style={{ borderColor: "var(--border)", color: "var(--text-2)" }}>
          <span>© {new Date().getFullYear()} PointControl. All rights reserved.</span>
          <div className="flex items-center gap-4">
            <span>Built for the courtside.</span>
            <button data-testid="footer-admin-portal" onClick={() => nav("/admin")}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary-c transition-colors">
              <Lock className="w-3 h-3" /> Admin
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  const nav = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)", color: "var(--text)" }}>
      <Nav nav={nav} />
      <Hero nav={nav} />
      <UpcomingTournaments />
      <AdMarquee />
      <Products nav={nav} />
      <FeatureGrid />
      <ForPlayers nav={nav} />
      <Pricing nav={nav} />
      <Footer nav={nav} />
    </div>
  );
}
